import { TRANSFER_DAYS, formatFileSize, type TransferRead } from "@omnabase/shared";

/** The words of a transfer in the app (the files round, 2026-10-30; brief rules 1 and 2): the parts the browser cuts a big file into, the
 *  composer's line while it uploads, and the chip under the sent message. Pure, loaded with the screens; the upload loop itself
 *  (`transferUpload.ts`) is loaded only when a big file is picked. */
export const TRANSFER_TRIES = 3;
export type PartPlan = { partNumber: number; start: number; end: number };
/** the slices the server signed: `end` not included, the last one shorter */
export function partsOf(size: number, partSize: number): PartPlan[] {
  const out: PartPlan[] = [];
  for (let start = 0, n = 1; start < size; start += partSize, n++) out.push({ partNumber: n, start, end: Math.min(size, start + partSize) });
  return out;
}

export type TransferState = { name: string; size: number; state: "uploading" | "done" | "failed"; progress?: number };
/** "Enfold-7.1.6.zip · 20 MB · uploading 43 %" / "… · transfer, link valid 10 days after sending" / "Enfold-7.1.6.zip · The upload failed; try again" */
export function transferLine(s: TransferState): string {
  if (s.state === "failed") return `${s.name} · The upload failed; try again`;
  const head = `${s.name} · ${formatFileSize(s.size)}`;
  return s.state === "done" ? `${head} · transfer, link valid ${TRANSFER_DAYS} days after sending` : `${head} · uploading ${Math.round((s.progress ?? 0) * 100)} %`;
}

const shortDay = (iso: string) => new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(new Date(iso));
const downloadsWords = (n: number) => (n === 0 ? "not downloaded yet" : n === 1 ? "downloaded once" : `downloaded ${n} times`);
/** the sent message's chip: until when the link works and whether the customer fetched it; before the reply left, and after the removal */
export function transferChip(f: { size: number; transfer: Pick<TransferRead, "expiresAt" | "downloads" | "removedAt"> }, now: Date): string {
  const t = f.transfer;
  if (t.removedAt) return `Transfer · ${formatFileSize(f.size)} · removed after ${TRANSFER_DAYS} days`;
  if (!t.expiresAt) return "Transfer · link made when the reply leaves";
  if (new Date(t.expiresAt).getTime() <= now.getTime()) return `Transfer · link ended ${shortDay(t.expiresAt)} · ${downloadsWords(t.downloads)}`;
  return `Transfer · link valid until ${shortDay(t.expiresAt)} · ${downloadsWords(t.downloads)}`;
}
